export const checkinEn = {
  // --- check-in.tsx (manual check-in page) ---
  title: "Daily Check-in",
  titleEdit: "Edit Daily Check-in",
  successSaved: "Entry Saved!",
  successUpdated: "Entry Updated!",
  redirectDashboard: "Redirecting to dashboard...",
  redirectDayView: "Returning to day view...",
  loadingExisting: "Loading existing entry...",
  goBack: "Go back",
  editBanner:
    "You are editing the entry for {{date}}. Saving will replace this day's existing pain locations, medications, notes, and tags.",
  errorDefault: "Failed to save entry. Please try again.",
  noExistingEntry: "No existing entry was found for this date. You can create one now.",
  queuedOfflineHint: "Saved offline — will sync when connected.",

  agent: {
    phase: {
      listening: "Listening...",
      analyzing: "Analyzing your message...",
      extracting: "Extracting pain details...",
      awaiting_confirmation: "Ready for your review",
      saving: "Saving your changes...",
    },
    savedChanges_one: "Saved {{count}} change",
    savedChanges_other: "Saved {{count}} changes",
    errorFallback: "An error occurred",
  },

  // --- shared chat/copilot components ---
  chat: {
    title: "Check-in",
    reviewTitle: "Review & Save",
    backToChat: "Back to chat",
    closeCheckin: "Close check-in",
    saveErrorDefault: "Failed to save. Please try again.",
    savingProgress: "Saving... ({{saved}}/{{total}})",
    initialGreeting: "Hi! Tell me about your pain today. You can speak or type.",
    errorTitle: "Error",
    inputPlaceholder: "Tell me about your pain...",
    reviewTitleWithCount_one: "Review & Save ({{count}} item)",
    reviewTitleWithCount_other: "Review & Save ({{count}} items)",
    ariaTextInput: "Text input",
    ariaSendMessage: "Send message",
    ariaSwitchToVoice: "Switch to voice input",
    ariaSwitchToText: "Switch to text input",
    ariaStructuredFormEntry: "Switch to structured form entry",
    useFormInstead: "Use form instead",
    modeSwitchedToVoice: "Switched to voice input mode",
    modeSwitchedToText: "Switched to text input mode",
    assistantAdded_one: "Got it! I've added that to your check-in.",
    assistantAdded_other: "Got it! I've added {{count}} items to your check-in.",
    assistantNoData: "I didn't catch any pain data. Could you tell me more about your pain?",
    assistantError: "Sorry, I had trouble processing that. {{message}}. Please try again.",
    // Close warning dialog
    closeWarningTitle: "Unsaved Changes",
    closeWarningDescription: "You have unsaved changes. Would you like to save them before closing?",
    closeWarningSaveAll: "Save All & Close",
    closeWarningDiscard: "Discard & Close",
    closeWarningCancel: "Cancel",
    closeWarningSaving: "Saving...",
    // Completion
    closeCheckinButton: "Close Check-in",
    // Unsaved changes badge
    unsavedCount_one: "{{count}} unsaved entry",
    unsavedCount_other: "{{count}} unsaved entries",
    // Tag chips
    suggestedTags: "Tags",
    // Chat message timestamps
    timestampJustNow: "Just now",
    timestampMinutesAgo: "{{count}}m ago",
    timestampHoursAgo: "{{count}}h ago",
    timestampDaysAgo: "{{count}}d ago",
    // Typing indicator
    assistantTyping: "Assistant is typing",
    // Voice indicator
    voiceInputLabel: "Voice input",
    voiceInputBadge: "Voice",
  },

  // --- update-set-review-panel.tsx / update-set-display.tsx / exit-warning-dialog.tsx ---
  review: {
    // Review panel header & empty state
    reviewAndSave: "Review & Save",
    noItemsToReview: "No items to review",
    bodyHeatmapToday: "Body Heatmap (Today)",
    saving: "Saving...",
    unknownItem: "Unknown item",
    // Item actions
    edit: "Edit",
    remove: "Remove",
    // Save button (with count)
    saveAll: "Save All",
    saveCount_one: "Save {{count}} item",
    saveCount_other: "Save {{count}} items",
    // Field labels (pain location details)
    locationLabel: "Location:",
    intensityLabel: "Intensity:",
    qualitiesLabel: "Qualities:",
    notesLabel: "Notes:",
    // Field labels (medication details)
    medicationLabel: "Medication:",
    doseLabel: "Dose:",
    timeLabel: "Time:",
    // Field labels (event details)
    eventLabel: "Event:",
    typeLabel: "Type:",
    relatedMedicationLabel: "Related Medication:",
    confidenceLabel: "Confidence:",
    // Field labels (source)
    sourceLabel: "Source:",
    // Update set display headings
    pendingUpdates: "Pending Updates",
    previousSnapshot: "Previous Snapshot",
    expand: "Expand",
    collapse: "Collapse",
    notes: "Notes",
    questions: "Questions",
    updated: "updated",
    expandPreviousItems: "Expand previous items",
    // Item type labels (aria/accessibility)
    itemTypePainLocation: "Pain Location",
    itemTypeMedication: "Medication",
    itemTypeEvent: "Event",
    itemTypeItem: "Item",
  },

  // --- exit-warning-dialog.tsx ---
  exitWarning: {
    title: "Discard check-in?",
    description: "You have unsaved changes:",
    discard: "Discard",
    keepEditing: "Keep Editing",
    // Item count labels (for bullet list)
    painEntry_one: "{{count}} pain entry",
    painEntry_other: "{{count}} pain entries",
    medication_one: "{{count}} medication",
    medication_other: "{{count}} medications",
    event_one: "{{count}} event",
    event_other: "{{count}} events",
  },

  clarification: {
    title: "Clarification needed",
    questionProgress: "Question {{progress}}",
    helperText: "Please answer the question above or say more about your pain.",
    aria: "Clarification needed: {{question}}",
    ariaWithProgress: "Clarification needed: {{question}} (Question {{progress}})",
  },

  // --- AI check-in (CopilotKit + voice input) ---
  copilot: {
    headerTitle: "AI Check-in",
    closeAriaLabel: "Close AI check-in",
    title: "Pain Tracker Assistant",
    placeholder: "Describe your pain or type a message...",
    footerSaved: "Your data has been saved automatically",
    footerPrompt: "Describe your pain to log it",
    sendAriaLabel: "Send message",
    continuing: "Continuing...",
    progressAnalyzingPain: "Analyzing your pain description...",
    progressAnalyzingGeneric: "Understanding your message...",
    // Entry hero greetings (time-of-day)
    greetingMorning: "Good morning",
    greetingAfternoon: "Good afternoon",
    greetingEvening: "Good evening",
    greetingRoughDay: "How are you holding up today?",
    // Entry hero intro & labels
    intro: "How are you feeling today? Tap the mic and tell me about your pain, medications, or anything on your mind.",
    recentPatterns: "Recent patterns",
    aboutRegion: "About {{region}}",
    stopRecording: "Stop recording",
    startVoiceRecording: "Start voice recording",
    hideWritingInput: "Hide writing input",
    orTypeInstead: "or type instead",
    audioConsentRequired:
      "Audio transcription is disabled. Enable consent in Settings to record your voice.",
    contextFetched: "Context fetched",
    retry: "Retry",
    autoRetrying: "Connection issue — retrying...",
    convertingSpeech: "Converting speech to text...",
    listening: "Listening...",
    transcribing: "Transcribing...",
    describePattern: "Describe",
    addFollowUp: "Add a follow-up...",
    describePain: "Describe your pain...",
    closeCheckin: "Close Check-in",
    suggestedTags: "Tags",
    // Pattern detail options
    patternGettingWorse: "Getting worse",
    patternGettingBetter: "Getting better",
    patternStayingSame: "Staying the same",
    patternNewPainHere: "New pain here",
    patternWorseSentence: "My {{region}} pain is getting worse.",
    patternBetterSentence: "My {{region}} pain is getting better.",
    patternSameSentence: "My {{region}} pain is staying about the same.",
    patternNewAreaSentence: "I am noticing new pain in my {{region}} today.",
    // Contextual follow-up prompts
    followUpFeelsWorse: "This feels worse than before.",
    followUpFeelsBetter: "This feels a bit better.",
    followUpStayingSame: "This is staying about the same.",
    followUpNewPainArea: "This is a new pain area.",
    followUpPainIsNow: "The pain is now...",
    followUpDosageWas: "The dosage was...",
    followUpSleepWas: "Sleep was...",
    followUpFatigueWas: "Fatigue was around...",
    followUpActivityLevel: "My activity level was...",
    followUpGetsWorseWhen: "It gets worse when...",
    followUpStartedAfter: "It started after...",
    followUpBeganOn: "This began on...",
    followUpSymptomsChanged: "How have my symptoms changed over the past month?",
    followUpFatigueTrend: "Do you see a trend in fatigue or cognitive fog?",
  },
  copilotVoice: {
    recentPatterns: "Recent Patterns",
    ariaStartRecording: "Start recording",
    ariaStopRecording: "Stop recording",
    placeholderListening: "Listening...",
    placeholderConverting: "Converting speech to text...",
    placeholderDefault: "Describe your pain or type a message...",
    statusConverting: "Converting speech to text...",
    cancelRecording: "Cancel",
    errorNoSpeech: "No speech detected. Try again and speak a bit louder.",
    errorTranscriptionFailed: "Transcription failed. Please try again.",
    errorMicrophone: "Microphone error. Please check permissions and try again.",
    errorStartRecording: "Could not start recording.",
    errorStopRecording: "Could not stop recording.",
    errorUnavailable: "Microphone unavailable. Please check permissions.",
    contextChipBodyRegion: "I've had {{thing}} pain lately.",
    contextChipNoticed: "I've noticed {{thing}} lately.",
  },

  // --- audio/audio-recorder.tsx ---
  audio: {
    reviewTitle: "Review Your Recording",
    reviewSubtitle: "Listen to make sure everything sounds right",
    unavailableTitle: "Audio recording unavailable",
    unavailableDescription:
      "Voice recording requires a secure connection and a supported browser.",
    textFallback: "You can use text entry instead",
    errorTitle: "Recording error",
    tryAgain: "Try Again",
    processing: "Processing recording...",
    requestingMicTitle: "Requesting microphone access",
    requestingMicDescription:
      "Please allow microphone access in your browser to continue.",
    recordingInProgress: "Recording in progress...",
    discard: "Discard",
    stop: "Stop",
    tapToRecord: "Tap to record",
    tapToRecordDescription:
      "Tell us about your pain. Speak naturally and take your time.",
    maxDuration: "Maximum recording length: {{minutes}} minutes",
    startRecording: "Start recording",
    discardRecording: "Discard recording",
    stopRecording: "Stop recording",
    recordingDuration: "Recording duration: {{duration}}",
    audioLevelIndicator: "Audio level indicator",
    /** Accessible live-region announcement when processing audio */
    statusProcessing: "Processing your recording, please wait.",
    /** Accessible live-region announcement on recording error */
    statusError: "Recording error occurred.",
  },

  // --- audio/audio-player.tsx ---
  player: {
    playbackError: "Playback Error",
    reRecord: "Re-record",
    useRecording: "Use this recording",
    pauseAudio: "Pause audio",
    playAudio: "Play audio",
    playbackProgress: "Audio playback progress",
    playbackPosition: "{{current}} of {{duration}}",
    /** Accessible live-region announcement on playback error */
    statusPlaybackError: "Audio playback error occurred.",
  },

  // --- audio/transcription-display.tsx ---
  transcription: {
    failed: "Transcription failed",
    failedDescription:
      "We couldn't transcribe your recording. Please try again.",
    tryAgain: "Try again",
    editTranscript: "Edit transcript",
    enterTranscript: "Enter transcript text...",
    saveShortcut: "Cmd+Enter to save, Esc to cancel",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    pausePlayback: "Pause playback",
    playRecording: "Play recording",
    confidenceLabel: "Transcription confidence: {{percentage}}",
    lowConfidenceWarning:
      "Some parts of this transcription may be inaccurate. Please review and edit if needed.",
    /** Accessible live-region announcement when transcription is loading */
    statusLoading: "Transcription in progress, please wait.",
    /** Accessible live-region announcement on transcription error */
    statusError: "Transcription failed.",
  },

  // --- audio/voice-check-in.tsx ---
  voice: {
    uploading: "Uploading recording...",
    transcribing: "Transcribing your voice...",
    pleaseWait: "This may take a moment",
    reRecord: "Re-record",
    useTranscription: "Use this transcription",
    tryAgain: "Try again",
    cancel: "Cancel",
    errorFallback: "An error occurred",
    /** Accessible status announcement for upload/transcribe progress */
    statusUploading: "Uploading your recording, please wait.",
    statusTranscribing: "Transcribing your voice, please wait.",
  },

  // --- extraction/extraction-review-panel.tsx ---
  extraction: {
    reviewTitle: "Review Your Check-in",
    reviewDescription: "We found {{summary}}. Review and confirm the items below.",
    confirmAllHigh: "Confirm all high confidence",
    clearAll: "Clear all",
    clarificationNeeded: "Clarification needed ({{count}})",
    painLocations: "Pain Locations",
    medications: "Medications",
    events: "Events",
    notes: "Notes",
    notesPlaceholder: "Additional notes or context...",
    back: "Back",
    cancel: "Cancel",
    saveCount: "Save {{count}} items",
    save: "Save",
    emptyTitle: "No data extracted",
    emptyDescription:
      "No data was extracted from your recording. Try again with a clearer description or use manual entry.",
    edited: "Edited",
    confirmItem: "Confirm item",
    editItem: "Edit item",
    removeItem: "Remove item",
  },

  // --- extraction/extraction-review-card.tsx ---
  reviewCard: {
    confirmed: "Confirmed",
    edited: "Edited",
    highConfidence: "High confidence",
    mediumConfidence: "Medium confidence",
    lowConfidence: "Low confidence",
    confirmItem: "Confirm item",
    editItem: "Edit item",
    removeItem: "Remove item",
  },

  // --- change-review card labels & actions ---
  changeReview: {
    // Shared card actions
    skip: "Skip",
    skipAll: "Skip All",
    edit: "Edit",
    save: "Save",
    saveAll: "Save All",
    saving: "Saving...",
    // Carousel header
    reviewChanges: "Review Changes ({{current}} of {{total}})",
    updated: "updated",
    updatedTooltip: "A newer message updated a previously extracted entry",
    swipeHint: "Swipe left or right to review each item",
    noChangesToReview: "No changes to review",
    // Carousel nav
    previousChange: "Previous change",
    nextChange: "Next change",
    carouselSlides: "Carousel slides",
    // Type summary pills
    painType: "Pain ({{count}})",
    wellbeingType: "Wellbeing ({{count}})",
    medsType: "Meds ({{count}})",
    eventsType: "Events ({{count}})",
    // Card type badges
    painEntry: "Pain Entry",
    medication: "Medication",
    medicationUpdate: "Medication update",
    event: "Event",
    wellbeing: "Wellbeing",
    lowConfidence: "Low confidence",
    noName: "(no name)",
    // Medication update operation labels
    operationStart: "Start",
    operationStop: "Stop",
    operationSwitch: "Switch",
    operationEdit: "Edit",
    operationAdherence: "Log adherence",
    // Wellbeing card
    fatigue: "Fatigue",
    brainFog: "Brain fog",
    activityLabel: "Activity:",
    functionalImpact: "Functional Impact",
    adjustValues: "Adjust values",
    looksGood: "Looks good",
    // Body region labels (used in cards and utils)
    bodyRegion: {
      head_neck: "Head & Neck",
      shoulder: "Shoulder",
      upper_back: "Upper Back",
      lower_back: "Lower Back",
      chest: "Chest",
      abdomen: "Abdomen",
      hip: "Hip",
      arm: "Arm",
      hand: "Hand",
      leg: "Leg",
      foot: "Foot",
      pelvis_groin: "Pelvis/Groin",
      generalized: "Generalized",
      other: "Other",
    },
    // Laterality prefixes
    laterality: {
      left: "Left",
      right: "Right",
      bilateral: "Both",
      midline: "",
      unknown: "",
    },
    // Event type labels
    eventType: {
      medication_start: "Medication",
      medication_dose_change: "Dose Change",
      medication_switch: "Medication Switch",
      medication_adherence_note: "Adherence Note",
      surgery_procedure: "Surgery/Procedure",
      injection: "Injection",
      therapy_session: "Therapy Session",
      medical_device: "Medical Device",
      injury: "Injury",
      illness: "Illness",
      flare: "Flare-up",
      symptom_change: "Symptom Change",
      activity_change: "Activity Change",
      diet_change: "Diet Change",
      sleep_change: "Sleep Change",
      stress_event: "Stress Event",
      weather_season: "Weather/Season",
      travel: "Travel",
      custom: "Custom Event",
    },
    // Time of day labels
    timeOfDay: {
      morning: "Morning",
      afternoon: "Afternoon",
      evening: "Evening",
      night: "Night",
    },
    // Relative date labels
    today: "Today",
    yesterday: "Yesterday",
    // Status labels
    statusConfirmed: "Confirmed",
    statusEdited: "Edited",
    statusSkipped: "Skipped",
    statusPending: "Pending",
    // Change type labels (for summaries)
    changeTypePain: "Pain Entry",
    changeTypeMedication: "Medication",
    changeTypeMedUpdate: "Medication Update",
    changeTypeEvent: "Event",
    changeTypeWellbeing: "Wellbeing",
    changeTypeUnknown: "Unknown",
    // Summary helper labels
    noChanges: "No changes",
    // Functional impact dimension labels
    dimension: {
      sleep: "Sleep",
      mood: "Mood",
      mobility: "Mobility",
      generalActivity: "General Activity",
      enjoymentOfLife: "Enjoyment of Life",
      work: "Work",
      relationships: "Relationships",
    },
    // Pacing option labels
    pacing: {
      rest: "Rest",
      light: "Light",
      moderate: "Moderate",
      heavy: "Heavy",
    },
    // Slider scale labels
    scaleNone: "None",
    scaleSevere: "Severe",
    fatigueLow: "No fatigue",
    fogLow: "No fog",
    impactHint: "0 = no interference, 10 = completely interferes",
    // Progressive disclosure toggle
    showFewerImpacts: "Show fewer impacts",
    addMoreImpacts: "Add {{count}} more impacts",
    // Accessibility
    dotCurrent: "Change {{index}} of {{total}} (current)",
    dotGoTo: "Go to change {{index}} of {{total}}",
    carouselLabel: "Review {{count}} change extracted by AI",
    carouselLabelPlural: "Review {{count}} changes extracted by AI",
    slideAnnouncement: "Showing change {{index}} of {{total}}",
    // Conflict resolution dialog
    conflictTitle: "Conflicting Values",
    conflictDescription: "Some values already have a manual entry for today. Choose which value to keep for each.",
    keepExisting: "Keep manual",
    useProposed: "Use AI value",
    keepAllExisting: "Keep all manual",
    useAllProposed: "Use all AI values",
    saveWithChoices: "Save with choices",
    conflictCancel: "Cancel",
    conflictExisting: "Manual",
    conflictProposed: "AI value",
    conflictPain: "Pain",
    conflictWellbeing: "Wellbeing",
    conflictMedication: "Medication",
  },

  // --- change-review/edit-*-dialog.tsx ---
  reviewEdit: {
    shared: {
      date: "Date",
      notes: "Notes",
      additionalDetails: "Additional details...",
      saveChanges: "Save Changes",
    },
    pain: {
      title: "Edit Pain Entry",
      description: "Update the extracted pain details before saving this entry.",
      bodyRegion: "Body Region",
      selectRegion: "Select region",
      side: "Side",
      selectSide: "Select side",
      intensity: "Intensity",
      intensityAriaLabel: "Pain intensity",
      qualities: "Pain Qualities",
      notes: "Notes",
    },
    medication: {
      title: "Edit Medication",
      description: "Update the extracted medication details before saving this item.",
      name: "Medication Name",
      namePlaceholder: "Enter medication name",
      nameComboboxAria: "Medication name with suggestions",
      dose: "Dose",
      dosePlaceholder: "e.g., 500",
      unit: "Unit",
      selectUnit: "Select unit",
      timeOfDay: "Time of Day",
      selectTime: "Select time",
    },
    event: {
      title: "Edit Event",
      description: "Update the extracted event details before saving this item.",
      titlePlaceholder: "Brief description of the event",
    },
    wellbeing: {
      title: "Edit Wellbeing",
      description: "Update the extracted wellbeing details before saving.",
      fatigue: "Fatigue",
      cognitiveFog: "Cognitive Fog",
      activityPacing: "Activity Level",
      functionalImpact: "Functional Impact",
      selectPacing: "Select activity level",
    },
  },

  // --- pain-entry/pain-entry-form.tsx ---
  painForm: {
    title: "Log Your Pain",
    editing: "Editing {{date}}",
    painFreeDaySelected: "Pain-free day selected",
    logPainFreeDay: "Log a pain-free day",
    tapAgainToCopy: "Tap again to copy",
    copyYesterday: "Copy yesterday",
    noEntryYesterday: "No entry yesterday",
    painFreeHint: "Tracking good days helps identify patterns and what helps your pain.",
    selectDate: "Select date",
    pastEntry: "Past entry",
    dateHint: "You can log pain for today or up to a year in the past",
    tapWhereItHurts: "Tap where it hurts",
    tapBodyPartsHint: "Tap body parts to add them. Colors show pain intensity.",
    swipeHint: "Use Front/Back buttons or swipe left/right. Tap body parts to add them.",
    front: "Front",
    back: "Back",
    diagramInstruction: "Click a body region to configure pain for it",
    diagramInstructionMobile: "Tap a body area to set pain, then tap OK. On back, choose left/center/right if needed. Use Front/Back buttons or swipe.",
    painLocations: "Pain Locations",
    noPainLocations: "No pain locations added yet.",
    addLocationHint: "Tap on the body diagram or use the form below to add locations.",
    bodyRegion: "Body region",
    surface: "Surface",
    addLocation: "Add Location",
    addPainLocation: "Add pain location",
    medicationsOptional: "Medications (optional)",
    notesOptional: "Notes (optional)",
    tagsOptional: "Tags (optional)",
    optional: "optional",
    done: "Done",
    quickAdjust: {
      title: "Pain for {{location}}",
      description: "Set pain intensity and add optional notes for this area.",
      intensityLabel: "Pain intensity",
      notesLabel: "Notes (optional)",
      confirm: "OK",
      confirmMany: "Add {{count}} locations",
      sideLabel: "Side",
      sideHint: "Select which side of the back is affected.",
      sideHintMany: "Select all affected back sides. The same pain details will be used for each.",
    },
    qualities: {
      label: "How does it feel?",
      placeholder: "How does it feel?",
      optionsAriaLabel: "Pain quality options",
    },
    locationNotes: {
      add: "Add notes",
      placeholder: "Describe this pain in more detail (what triggers it, when it's worse, etc.)",
      inputAriaLabel: "Location notes input",
      clear: "Clear",
    },
    medicationInput: {
      title: "Pain medications",
      addMedication: "Add medication",
      helpText: "Add one-off medications you took for pain today.",
      oneOffInfo: "These are one-off pain medications for this check-in. Ongoing medications belong in the Medications tab.",
      oneOffInfoAria: "Show one-off medication guidance",
      tookTitle: "Took {{name}}",
      noActiveMedications: "No medications added yet",
      namePlaceholder: "Medication name (e.g., Ibuprofen)",
      nameComboboxAria: "Medication name with suggestions",
      suggestionUsedCount: "used {{count}}x",
      noSuggestions: "No matches — type to add new",
      suggestionsSectionLabel: "Previous medications",
      removeMedicationAria: "Remove medication",
      dose: "Dose",
      unit: "Unit",
      when: "When",
      notes: "Notes",
      addNotes: "Add notes",
      notesPlaceholder: "Optional details (e.g. Taken yesterday, helped after 30 min)",
      // Start-medication sheet
      startMedication: {
        title: "Add One-Off Medication",
        description: "This logs a one-off medication you took for pain and creates a matching event.",
        name: "Medication name",
        namePlaceholder: "e.g., Ibuprofen",
        dose: "Dose",
        dosePlaceholder: "e.g., 200",
        unit: "Unit",
        selectUnit: "Unit",
        when: "Time of day",
        selectTime: "When",
        save: "Add medication",
        cancel: "Cancel",
        saving: "Saving...",
      },
      // Stop medication dialog (inline check-in)
      stopMedication: {
        title: "Stop Medication",
        description: "Set the date this medication was stopped.",
        dateLabel: "Stop date",
        confirm: "Stop medication",
        confirming: "Stopping...",
        cancel: "Cancel",
        stopAria: "Stop {{name}}",
      },
      // Switch medication dialog (inline check-in)
      switchMedication: {
        title: "Switch Medication",
        description: "Switch from {{from}} to a new medication.",
        dateLabel: "Switch date",
        toLabel: "To (new medication)",
        toPlaceholder: "Search or type medication name...",
        notesLabel: "Notes (optional)",
        notesPlaceholder: "Any notes about the switch...",
        confirm: "Switch medication",
        confirming: "Switching...",
        cancel: "Cancel",
        switchAria: "Switch {{name}}",
      },
      // Overflow menu
      moreActions: "More actions for {{name}}",
      // Adherence note dialog (T2.11)
      adherenceNote: {
        menuItem: "Log adherence note",
        title: "Log Adherence Note",
        description: "Record a note about taking {{name}}.",
        noteLabel: "Note",
        notePlaceholder: "e.g., Missed morning dose due to travel",
        submit: "Save note",
        submitting: "Saving...",
        cancel: "Cancel",
      },
    },
    tagsInput: {
      title: "Tags (optional)",
      placeholder: "Type a tag and press Enter...",
      addTagAria: "Add tag",
      removeTagAria: "Remove {{tag}} tag",
      suggestions: "Suggestions:",
      count: "{{count}}/{{max}} tags selected",
      maxReached: "(max reached)",
      commonTags: {
        migraine: "migraine",
        "flare-up": "flare-up",
        "high stress": "high stress",
        "poor sleep": "poor sleep",
        "weather change": "weather change",
        exercise: "exercise",
        "work day": "work day",
        travel: "travel",
        menstrual: "menstrual",
        anxiety: "anxiety",
        fatigue: "fatigue",
        "good day": "good day",
      },
    },
    notesPlaceholder: "Add any additional notes about your pain, what might have caused it, or what helped...",
    filledCount: "{{count}} filled",
    savePainFreeDay: "Save Pain-Free Day",
    locationSingle: "location",
    locationPlural: "locations",
    typeSingle: "type",
    typePlural: "types",
    removeLocation: "Remove {{location}}",
    // Daily Impact section (functional impact + fibromyalgia symptoms)
    dailyImpact: {
      sectionTitle: "Daily Impact (optional)",
      sectionDescription: "How did pain affect your day?",
      functionalImpactLabel: "Functional Impact",
      dimensions: {
        sleep: "Sleep",
        mood: "Mood",
        mobility: "Mobility",
        generalActivity: "General Activity",
        enjoymentOfLife: "Enjoyment of Life",
        work: "Work",
        relationships: "Relationships",
      },
      dimensionScale: {
        none: "No impact",
        full: "Complete",
      },
      symptomsLabel: "Other Symptoms",
      fatigue: "Fatigue",
      cognitiveFog: "Cognitive Fog",
      nausea: "Nausea",
      vomiting: "Vomiting",
      weakness: "Weakness",
      fatigueScale: {
        none: "None",
        full: "Severe",
      },
      fogScale: {
        none: "None",
        full: "Severe",
      },
      activityPacing: "Activity Level",
      pacingLevels: {
        rest: "Rest",
        light: "Light",
        moderate: "Moderate",
        heavy: "Heavy",
      },
    },
    intensity: {
      none: "No pain",
      mild: "Mild",
      moderate: "Moderate",
      severe: "Severe",
      worst: "Worst imaginable",
      worstShort: "Worst",
    },
    errors: {
      duplicateLocation: "This body region and side combination is already added",
      missingLocation: "Please add at least one pain location, or log a pain-free day",
      saveFailed: "Failed to save pain entry",
    },
  },

  // --- Validation errors (use-copilot-pain-actions.ts, copilot-action-handlers.ts) ---
  validation: {
    intensityRange: "Intensity must be between 0 and 10",
    locationRequired: "Pain location needs region or zone",
    invalidBodyRegion: "Invalid body region: {{value}}",
    invalidBodyZone: "Invalid body zone: {{value}}",
    invalidLaterality: "Invalid laterality: {{value}}",
    invalidSide: "Invalid side: {{value}}",
    invalidSurface: "Invalid surface: {{value}}",
    medicationNameRequired: "Medication name is required",
    invalidTimeOfDay: "Invalid time of day: {{value}}",
    invalidEventType: "Invalid event type: {{value}}. Use one of: {{validTypes}}",
    eventTitleRequired: "Event title is required",
    eventTitleMaxLength: "Event title must be 100 characters or less",
    invalidConfidenceLevel: "Invalid confidence level: {{value}}",
  },

  // --- Hook-level messages (use-pain-entry.ts, use-save-update-set.ts) ---
  hooks: {
    failedToSave: "Failed to save",
  },
} as const;

export const checkinFi = {
  // --- check-in.tsx ---
  title: "Päivittäinen kirjaus",
  titleEdit: "Muokkaa päivittäistä kirjausta",
  successSaved: "Kirjaus tallennettu!",
  successUpdated: "Kirjaus päivitetty!",
  redirectDashboard: "Siirrytään koontinäkymään...",
  redirectDayView: "Palataan päivänäkymään...",
  loadingExisting: "Ladataan olemassa olevaa kirjausta...",
  goBack: "Takaisin",
  editBanner:
    "Muokkaat kirjausta päivälle {{date}}. Tallennus korvaa päivän olemassa olevat kipupaikat, lääkkeet, muistiinpanot ja tagit.",
  errorDefault: "Kirjauksen tallennus epäonnistui. Yritä uudelleen.",
  noExistingEntry:
    "Tälle päivälle ei löytynyt olemassa olevaa kirjausta. Voit luoda sellaisen nyt.",
  queuedOfflineHint: "Tallennettu offline-tilassa — synkronoidaan kun yhteys palaa.",

  agent: {
    phase: {
      listening: "Kuunnellaan...",
      analyzing: "Analysoidaan viestiäsi...",
      extracting: "Poimitaan kiputiedot...",
      awaiting_confirmation: "Valmis tarkistettavaksi",
      saving: "Tallennetaan muutoksia...",
    },
    savedChanges_one: "Tallennettiin {{count}} muutos",
    savedChanges_other: "Tallennettiin {{count}} muutosta",
    errorFallback: "Tapahtui virhe",
  },

  // --- shared chat/copilot components ---
  chat: {
    title: "Kirjaus",
    reviewTitle: "Tarkista ja tallenna",
    backToChat: "Takaisin keskusteluun",
    closeCheckin: "Sulje kirjaus",
    saveErrorDefault: "Tallennus epäonnistui. Yritä uudelleen.",
    savingProgress: "Tallennetaan... ({{saved}}/{{total}})",
    initialGreeting:
      "Hei! Kerro kivuistasi tänään. Voit puhua tai kirjoittaa.",
    errorTitle: "Virhe",
    inputPlaceholder: "Kerro kivustasi...",
    reviewTitleWithCount_one: "Tarkista ja tallenna ({{count}} kohde)",
    reviewTitleWithCount_other: "Tarkista ja tallenna ({{count}} kohdetta)",
    ariaTextInput: "Tekstinsyöttö",
    ariaSendMessage: "Lähetä viesti",
    ariaSwitchToVoice: "Vaihda äänisyöttöön",
    ariaSwitchToText: "Vaihda tekstisyöttöön",
    ariaStructuredFormEntry: "Vaihda lomakkeeseen",
    useFormInstead: "Käytä lomaketta",
    modeSwitchedToVoice: "Vaihdettu äänisyöttöön",
    modeSwitchedToText: "Vaihdettu tekstisyöttöön",
    assistantAdded_one: "Selvä! Lisäsin sen kirjaukseesi.",
    assistantAdded_other: "Selvä! Lisäsin {{count}} kohdetta kirjaukseesi.",
    assistantNoData: "En saanut kiinni kiputiedoista. Voitko kertoa lisää kivustasi?",
    assistantError: "Pahoittelut, käsittelyssä oli ongelma. {{message}}. Yritä uudelleen.",
    // Close warning dialog
    closeWarningTitle: "Tallentamattomia muutoksia",
    closeWarningDescription: "Sinulla on tallentamattomia muutoksia. Haluatko tallentaa ne ennen sulkemista?",
    closeWarningSaveAll: "Tallenna kaikki ja sulje",
    closeWarningDiscard: "Hylkää ja sulje",
    closeWarningCancel: "Peruuta",
    closeWarningSaving: "Tallennetaan...",
    // Completion
    closeCheckinButton: "Sulje kirjaus",
    // Unsaved changes badge
    unsavedCount_one: "{{count}} tallentamaton kirjaus",
    unsavedCount_other: "{{count}} tallentamatonta kirjausta",
    // Tag chips
    suggestedTags: "Tagit",
    // Chat message timestamps
    timestampJustNow: "Juuri nyt",
    timestampMinutesAgo: "{{count}} min sitten",
    timestampHoursAgo: "{{count}} t sitten",
    timestampDaysAgo: "{{count}} pv sitten",
    // Typing indicator
    assistantTyping: "Avustaja kirjoittaa",
    // Voice indicator
    voiceInputLabel: "Äänisyöte",
    voiceInputBadge: "Ääni",
  },

  // --- update-set-review-panel.tsx / update-set-display.tsx / exit-warning-dialog.tsx ---
  review: {
    // Review panel header & empty state
    reviewAndSave: "Tarkista ja tallenna",
    noItemsToReview: "Ei tarkistettavia kohteita",
    bodyHeatmapToday: "Kehon kipukartta (tänään)",
    saving: "Tallennetaan...",
    unknownItem: "Tuntematon kohde",
    // Item actions
    edit: "Muokkaa",
    remove: "Poista",
    // Save button (with count)
    saveAll: "Tallenna kaikki",
    saveCount_one: "Tallenna {{count}} kohde",
    saveCount_other: "Tallenna {{count}} kohdetta",
    // Field labels (pain location details)
    locationLabel: "Sijainti:",
    intensityLabel: "Voimakkuus:",
    qualitiesLabel: "Laatu:",
    notesLabel: "Muistiinpanot:",
    // Field labels (medication details)
    medicationLabel: "Lääke:",
    doseLabel: "Annos:",
    timeLabel: "Aika:",
    // Field labels (event details)
    eventLabel: "Tapahtuma:",
    typeLabel: "Tyyppi:",
    relatedMedicationLabel: "Liittyvä lääke:",
    confidenceLabel: "Luottamus:",
    // Field labels (source)
    sourceLabel: "Lähde:",
    // Update set display headings
    pendingUpdates: "Odottavat päivitykset",
    previousSnapshot: "Edellinen tilannekuva",
    expand: "Laajenna",
    collapse: "Pienennä",
    notes: "Muistiinpanot",
    questions: "Kysymykset",
    updated: "päivitetty",
    expandPreviousItems: "Näytä edelliset kohteet",
    // Item type labels (aria/accessibility)
    itemTypePainLocation: "Kipupaikka",
    itemTypeMedication: "Lääke",
    itemTypeEvent: "Tapahtuma",
    itemTypeItem: "Kohde",
  },

  // --- exit-warning-dialog.tsx ---
  exitWarning: {
    title: "Hylätäänkö kirjaus?",
    description: "Sinulla on tallentamattomia muutoksia:",
    discard: "Hylkää",
    keepEditing: "Jatka muokkausta",
    // Item count labels (for bullet list)
    painEntry_one: "{{count}} kipukirjaus",
    painEntry_other: "{{count}} kipukirjausta",
    medication_one: "{{count}} lääke",
    medication_other: "{{count}} lääkettä",
    event_one: "{{count}} tapahtuma",
    event_other: "{{count}} tapahtumaa",
  },

  clarification: {
    title: "Tarkennusta tarvitaan",
    questionProgress: "Kysymys {{progress}}",
    helperText: "Vastaa yll\u00e4 olevaan kysymykseen tai kerro lis\u00e4\u00e4 kivustasi.",
    aria: "Tarkennusta tarvitaan: {{question}}",
    ariaWithProgress: "Tarkennusta tarvitaan: {{question}} (Kysymys {{progress}})",
  },

  // --- AI check-in (CopilotKit + voice input) ---
  copilot: {
    headerTitle: "AI-kirjaus",
    closeAriaLabel: "Sulje AI-kirjaus",
    title: "Kipuseuranta-avustaja",
    placeholder: "Kuvaile kipuasi tai kirjoita viesti...",
    footerSaved: "Tietosi on tallennettu automaattisesti",
    footerPrompt: "Kuvaile kipuasi kirjataksesi sen",
    sendAriaLabel: "Lähetä viesti",
    continuing: "Jatketaan...",
    progressAnalyzingPain: "Analysoidaan kipukuvausta...",
    progressAnalyzingGeneric: "Ymmärretään viestiä...",
    // Entry hero greetings (time-of-day)
    greetingMorning: "Hyvää huomenta",
    greetingAfternoon: "Hyvää iltapäivää",
    greetingEvening: "Hyvää iltaa",
    greetingRoughDay: "Miten jaksat tänään?",
    // Entry hero intro & labels
    intro: "Miltä sinusta tuntuu tänään? Napauta mikrofonia ja kerro kivusta, lääkityksestä tai mistä tahansa mielessäsi olevasta.",
    recentPatterns: "Viimeaikaiset kipukuvat",
    aboutRegion: "Tietoa alueesta {{region}}",
    stopRecording: "Pysäytä tallennus",
    startVoiceRecording: "Aloita äänitallennus",
    hideWritingInput: "Piilota tekstinsyöttö",
    orTypeInstead: "tai kirjoita sen sijaan",
    audioConsentRequired:
      "Äänilitterointi ei ole käytössä. Ota suostumus käyttöön asetuksissa, jotta voit äänittää.",
    contextFetched: "Konteksti haettu",
    retry: "Yritä uudelleen",
    autoRetrying: "Yhteysongelma — yritetään uudelleen...",
    convertingSpeech: "Muunnetaan puhetta tekstiksi...",
    listening: "Kuunnellaan...",
    transcribing: "Litteroidaan...",
    describePattern: "Kuvaile",
    addFollowUp: "Lisää tarkennus...",
    describePain: "Kuvaile kipuasi...",
    closeCheckin: "Sulje kirjaus",
    suggestedTags: "Tagit",
    // Pattern detail options
    patternGettingWorse: "Pahenee",
    patternGettingBetter: "Paranee",
    patternStayingSame: "Pysyy samana",
    patternNewPainHere: "Uutta kipua täällä",
    patternWorseSentence: "{{region}}kipu pahenee.",
    patternBetterSentence: "{{region}}kipu paranee.",
    patternSameSentence: "{{region}}kipu pysyy suunnilleen samana.",
    patternNewAreaSentence: "Huomaan tänään uutta kipua alueella {{region}}.",
    // Contextual follow-up prompts
    followUpFeelsWorse: "Tämä tuntuu pahemmalta kuin ennen.",
    followUpFeelsBetter: "Tämä tuntuu hieman paremmalta.",
    followUpStayingSame: "Tämä pysyy suunnilleen samana.",
    followUpNewPainArea: "Kipua on uudella alueella.",
    followUpPainIsNow: "Kipu on nyt...",
    followUpDosageWas: "Annos oli...",
    followUpSleepWas: "Uni oli...",
    followUpFatigueWas: "Väsymys oli noin...",
    followUpActivityLevel: "Päivä oli aktiivisuudeltaan...",
    followUpGetsWorseWhen: "Se pahenee kun...",
    followUpStartedAfter: "Se alkoi jälkeen...",
    followUpBeganOn: "Tämä alkoi päivänä...",
    followUpSymptomsChanged: "Miten vointini on muuttunut viimeisen kuukauden aikana?",
    followUpFatigueTrend: "Näetkö väsymyksen tai aivosumun trendin?",
  },
  copilotVoice: {
    recentPatterns: "Viimeaikaiset kipukuvat",
    ariaStartRecording: "Aloita tallennus",
    ariaStopRecording: "Pysäytä tallennus",
    placeholderListening: "Kuunnellaan...",
    placeholderConverting: "Muunnetaan puhetta tekstiksi...",
    placeholderDefault: "Kuvaile kipuasi tai kirjoita viesti...",
    statusConverting: "Muunnetaan puhetta tekstiksi...",
    cancelRecording: "Peruuta",
    errorNoSpeech: "Puhetta ei havaittu. Yritä uudelleen ja puhu hieman kovempaa.",
    errorTranscriptionFailed: "Litterointi epäonnistui. Yritä uudelleen.",
    errorMicrophone: "Mikrofonivirhe. Tarkista käyttöoikeudet ja yritä uudelleen.",
    errorStartRecording: "Tallennusta ei voitu aloittaa.",
    errorStopRecording: "Tallennusta ei voitu pysäyttää.",
    errorUnavailable: "Mikrofoni ei ole käytettävissä. Tarkista käyttöoikeudet.",
    contextChipBodyRegion: "Minulla on ollut viime aikoina kipua: {{thing}}.",
    contextChipNoticed: "Olen huomannut viime aikoina: {{thing}}.",
  },

  // --- audio/audio-recorder.tsx ---
  audio: {
    reviewTitle: "Kuuntele ääninäytteesi",
    reviewSubtitle: "Varmista että kaikki kuulostaa oikealta",
    unavailableTitle: "Äänitallennus ei ole käytettävissä",
    unavailableDescription:
      "Äänitallennus vaatii suojatun yhteyden ja tuetun selaimen.",
    textFallback: "Voit käyttää tekstinsyöttöä",
    errorTitle: "Tallennusvirhe",
    tryAgain: "Yritä uudelleen",
    processing: "Käsitellään tallennetta...",
    requestingMicTitle: "Pyydetään mikrofonin käyttöoikeutta",
    requestingMicDescription:
      "Salli mikrofonin käyttö selaimessasi jatkaaksesi.",
    recordingInProgress: "Tallennus käynnissä...",
    discard: "Hylkää",
    stop: "Pysäytä",
    tapToRecord: "Napauta aloittaaksesi",
    tapToRecordDescription:
      "Kerro meille kivuistasi. Puhu luonnollisesti ja ota aikasi.",
    maxDuration: "Tallennuksen enimmäispituus: {{minutes}} minuuttia",
    startRecording: "Aloita tallennus",
    discardRecording: "Hylkää tallennus",
    stopRecording: "Pysäytä tallennus",
    recordingDuration: "Tallennuksen kesto: {{duration}}",
    audioLevelIndicator: "Äänitasoilmaisin",
    /** Saavutettava live-alueen ilmoitus äänen käsittelystä */
    statusProcessing: "Käsitellään tallennettasi, odota hetki.",
    /** Saavutettava live-alueen ilmoitus tallennusvirheestä */
    statusError: "Tallennusvirhe tapahtui.",
  },

  // --- audio/audio-player.tsx ---
  player: {
    playbackError: "Toistovirhe",
    reRecord: "Tallenna uudelleen",
    useRecording: "Käytä tätä tallennetta",
    pauseAudio: "Keskeytä toisto",
    playAudio: "Toista ääni",
    playbackProgress: "Äänentoiston eteneminen",
    playbackPosition: "{{current}} / {{duration}}",
    /** Saavutettava live-alueen ilmoitus toistovirheestä */
    statusPlaybackError: "Äänentoiston virhe tapahtui.",
  },

  // --- audio/transcription-display.tsx ---
  transcription: {
    failed: "Litterointi epäonnistui",
    failedDescription:
      "Tallennettasi ei voitu litteroida. Yritä uudelleen.",
    tryAgain: "Yritä uudelleen",
    editTranscript: "Muokkaa litterointia",
    enterTranscript: "Syötä litterointiteksti...",
    saveShortcut: "Cmd+Enter tallentaa, Esc peruuttaa",
    edit: "Muokkaa",
    save: "Tallenna",
    cancel: "Peruuta",
    pausePlayback: "Keskeytä toisto",
    playRecording: "Toista tallenne",
    confidenceLabel: "Litteroinnin luottamus: {{percentage}}",
    lowConfidenceWarning:
      "Jotkin tämän litteroinnin osat voivat olla epätarkkoja. Tarkista ja muokkaa tarvittaessa.",
    /** Saavutettava live-alueen ilmoitus litteroinnin latauksesta */
    statusLoading: "Litterointi käynnissä, odota hetki.",
    /** Saavutettava live-alueen ilmoitus litterointivirheestä */
    statusError: "Litterointi epäonnistui.",
  },

  // --- audio/voice-check-in.tsx ---
  voice: {
    uploading: "Ladataan tallennetta...",
    transcribing: "Litteroidaan puheesi...",
    pleaseWait: "Tämä voi kestää hetken",
    reRecord: "Tallenna uudelleen",
    useTranscription: "Käytä tätä litterointia",
    tryAgain: "Yritä uudelleen",
    cancel: "Peruuta",
    errorFallback: "Tapahtui virhe",
    /** Saavutettava tilailmoitus lataus-/litterointiedistymiselle */
    statusUploading: "Ladataan tallennettasi, odota hetki.",
    statusTranscribing: "Litteroidaan puhettasi, odota hetki.",
  },

  // --- extraction/extraction-review-panel.tsx ---
  extraction: {
    reviewTitle: "Tarkista kirjauksesi",
    reviewDescription:
      "Löysimme {{summary}}. Tarkista ja vahvista alla olevat kohteet.",
    confirmAllHigh: "Vahvista korkean luottamuksen kohteet",
    clearAll: "Tyhjennä kaikki",
    clarificationNeeded: "Tarkennusta tarvitaan ({{count}})",
    painLocations: "Kipupaikat",
    medications: "Lääkkeet",
    events: "Tapahtumat",
    notes: "Muistiinpanot",
    notesPlaceholder: "Lisätietoja tai kontekstia...",
    back: "Takaisin",
    cancel: "Peruuta",
    saveCount: "Tallenna {{count}} kohdetta",
    save: "Tallenna",
    emptyTitle: "Tietoja ei poimittu",
    emptyDescription:
      "Tallenteestasi ei poimittu tietoja. Yritä uudelleen selkeämmällä kuvauksella tai käytä manuaalista syöttöä.",
    edited: "Muokattu",
    confirmItem: "Vahvista kohde",
    editItem: "Muokkaa kohdetta",
    removeItem: "Poista kohde",
  },

  // --- extraction/extraction-review-card.tsx ---
  reviewCard: {
    confirmed: "Vahvistettu",
    edited: "Muokattu",
    highConfidence: "Korkea luottamus",
    mediumConfidence: "Keskitason luottamus",
    lowConfidence: "Matala luottamus",
    confirmItem: "Vahvista kohde",
    editItem: "Muokkaa kohdetta",
    removeItem: "Poista kohde",
  },

  // --- change-review card labels & actions ---
  changeReview: {
    // Shared card actions
    skip: "Ohita",
    skipAll: "Ohita kaikki",
    edit: "Muokkaa",
    save: "Tallenna",
    saveAll: "Tallenna kaikki",
    saving: "Tallennetaan...",
    // Carousel header
    reviewChanges: "Tarkista muutokset ({{current}} / {{total}})",
    updated: "päivitetty",
    updatedTooltip: "Uudempi viesti päivitti aiemmin poimitun kirjauksen",
    swipeHint: "Pyyhkäise vasemmalle tai oikealle tarkistaaksesi jokaisen kohteen",
    noChangesToReview: "Ei tarkistettavia muutoksia",
    // Carousel nav
    previousChange: "Edellinen muutos",
    nextChange: "Seuraava muutos",
    carouselSlides: "Karusellin kortit",
    // Type summary pills
    painType: "Kipu ({{count}})",
    wellbeingType: "Hyvinvointi ({{count}})",
    medsType: "Lääkkeet ({{count}})",
    eventsType: "Tapahtumat ({{count}})",
    // Card type badges
    painEntry: "Kipukirjaus",
    medication: "Lääke",
    medicationUpdate: "Lääkemuutos",
    event: "Tapahtuma",
    wellbeing: "Hyvinvointi",
    lowConfidence: "Matala luottamus",
    noName: "(ei nimeä)",
    // Medication update operation labels
    operationStart: "Aloita",
    operationStop: "Lopeta",
    operationSwitch: "Vaihda",
    operationEdit: "Muokkaa",
    operationAdherence: "Kirjaa hoitomerkintä",
    // Wellbeing card
    fatigue: "Väsymys",
    brainFog: "Aivosumu",
    activityLabel: "Aktiivisuus:",
    functionalImpact: "Toimintavaikutus",
    adjustValues: "Tarkista arvot",
    looksGood: "OK",
    // Body region labels (used in cards and utils)
    bodyRegion: {
      head_neck: "Pää ja niska",
      shoulder: "Olkapää",
      upper_back: "Yläselkä",
      lower_back: "Alaselkä",
      chest: "Rintakehä",
      abdomen: "Vatsa",
      hip: "Lonkka",
      arm: "Käsivarsi",
      hand: "Käsi",
      leg: "Jalka",
      foot: "Jalkaterä",
      pelvis_groin: "Lantio ja nivunen",
      generalized: "Yleistynyt",
      other: "Muu",
    },
    // Laterality prefixes
    laterality: {
      left: "Vasen",
      right: "Oikea",
      bilateral: "Molemmat",
      midline: "Keskellä",
      unknown: "Ei määritelty",
    },
    // Event type labels
    eventType: {
      medication_start: "Lääkitys",
      medication_dose_change: "Annosmuutos",
      medication_switch: "Lääkityksen vaihto",
      medication_adherence_note: "Lääkityshuomio",
      surgery_procedure: "Leikkaus tai toimenpide",
      injection: "Injektio",
      therapy_session: "Terapiakerta",
      medical_device: "Lääkinnällinen laite",
      injury: "Vamma",
      illness: "Sairaus",
      flare: "Pahenemisvaihe",
      symptom_change: "Oiremuutos",
      activity_change: "Aktiivisuuden muutos",
      diet_change: "Ruokavaliomuutos",
      sleep_change: "Unimuutos",
      stress_event: "Stressitapahtuma",
      weather_season: "Sää tai vuodenaika",
      travel: "Matkustus",
      custom: "Muu tapahtuma",
    },
    // Time of day labels
    timeOfDay: {
      morning: "Aamu",
      afternoon: "Iltapäivä",
      evening: "Ilta",
      night: "Yö",
    },
    // Relative date labels
    today: "Tänään",
    yesterday: "Eilen",
    // Status labels
    statusConfirmed: "Vahvistettu",
    statusEdited: "Muokattu",
    statusSkipped: "Ohitettu",
    statusPending: "Odottaa",
    // Change type labels (for summaries)
    changeTypePain: "Kipukirjaus",
    changeTypeMedication: "Lääke",
    changeTypeMedUpdate: "Lääkemuutos",
    changeTypeEvent: "Tapahtuma",
    changeTypeWellbeing: "Hyvinvointi",
    changeTypeUnknown: "Tuntematon",
    // Summary helper labels
    noChanges: "Ei muutoksia",
    // Functional impact dimension labels
    dimension: {
      sleep: "Uni",
      mood: "Mieliala",
      mobility: "Liikkuminen",
      generalActivity: "Yleinen aktiivisuus",
      enjoymentOfLife: "Elämästä nauttiminen",
      work: "Työ",
      relationships: "Ihmissuhteet",
    },
    // Pacing option labels
    pacing: {
      rest: "Lepo",
      light: "Kevyt",
      moderate: "Kohtalainen",
      heavy: "Raskas",
    },
    // Slider scale labels
    scaleNone: "Ei mitään",
    scaleSevere: "Voimakas",
    fatigueLow: "Ei väsymystä",
    fogLow: "Ei sumua",
    impactHint: "0 = ei vaikutusta, 10 = täysin estää",
    // Progressive disclosure toggle
    showFewerImpacts: "Piilota lisäkentät",
    addMoreImpacts: "Lisää {{count}} vaikutuskenttää",
    // Accessibility
    dotCurrent: "Muutos {{index}} / {{total}} (nykyinen)",
    dotGoTo: "Siirry muutokseen {{index}} / {{total}}",
    carouselLabel: "Tarkista {{count}} tekoälyn poimima muutos",
    carouselLabelPlural: "Tarkista {{count}} tekoälyn poimimaa muutosta",
    slideAnnouncement: "Näytetään muutos {{index}} / {{total}}",
    // Conflict resolution dialog
    conflictTitle: "Ristiriitaiset arvot",
    conflictDescription: "Joillain arvoilla on jo manuaalinen kirjaus tälle päivälle. Valitse, kumpi arvo pidetään.",
    keepExisting: "Pidä nykyinen",
    useProposed: "Käytä AI-arvoa",
    keepAllExisting: "Pidä kaikki nykyiset",
    useAllProposed: "Käytä kaikkia AI-arvoja",
    saveWithChoices: "Tallenna valinnoilla",
    conflictCancel: "Peruuta",
    conflictExisting: "Nykyinen",
    conflictProposed: "AI-arvo",
    conflictPain: "Kipu",
    conflictWellbeing: "Hyvinvointi",
    conflictMedication: "Lääkitys",
  },

  // --- change-review/edit-*-dialog.tsx ---
  reviewEdit: {
    shared: {
      date: "Päivä",
      notes: "Muistiinpanot",
      additionalDetails: "Lisätietoja...",
      saveChanges: "Tallenna muutokset",
    },
    pain: {
      title: "Muokkaa kipukirjausta",
      description: "Päivitä poimitut kivun tiedot ennen tallennusta.",
      bodyRegion: "Kehon alue",
      selectRegion: "Valitse alue",
      side: "Puoli",
      selectSide: "Valitse puoli",
      intensity: "Voimakkuus",
      intensityAriaLabel: "Kivun voimakkuus",
      qualities: "Kivun laatu",
      notes: "Muistiinpanot",
    },
    medication: {
      title: "Muokkaa lääkettä",
      description: "Päivitä poimitut lääkkeen tiedot ennen tallennusta.",
      name: "Lääkkeen nimi",
      namePlaceholder: "Kirjoita lääkkeen nimi",
      nameComboboxAria: "Lääkkeen nimi ehdotuksilla",
      dose: "Annos",
      dosePlaceholder: "esim. 500",
      unit: "Yksikkö",
      selectUnit: "Valitse yksikkö",
      timeOfDay: "Vuorokaudenaika",
      selectTime: "Valitse aika",
    },
    event: {
      title: "Muokkaa tapahtumaa",
      description: "Päivitä poimitut tapahtuman tiedot ennen tallennusta.",
      titlePlaceholder: "Lyhyt kuvaus tapahtumasta",
    },
    wellbeing: {
      title: "Muokkaa hyvinvointia",
      description: "Päivitä poimitut hyvinvoinnin tiedot ennen tallennusta.",
      fatigue: "Väsymys",
      cognitiveFog: "Aivosumu",
      activityPacing: "Aktiivisuustaso",
      functionalImpact: "Toimintavaikutus",
      selectPacing: "Valitse aktiivisuustaso",
    },
  },

  // --- pain-entry/pain-entry-form.tsx ---
  painForm: {
    title: "Kirjaa kipu",
    editing: "Muokataan: {{date}}",
    painFreeDaySelected: "Kivuton päivä valittu",
    logPainFreeDay: "Kirjaa kivuton päivä",
    tapAgainToCopy: "Napauta uudelleen kopioidaksesi",
    copyYesterday: "Kopioi eilisestä",
    noEntryYesterday: "Ei kirjausta eiliseltä",
    painFreeHint: "Kivuttomien päivien seuranta auttaa tunnistamaan kaavoja ja kipua helpottavia tekijöitä.",
    selectDate: "Valitse päivä",
    pastEntry: "Mennyt kirjaus",
    dateHint: "Voit kirjata kivun tälle päivälle tai enintään vuoden taakse",
    tapWhereItHurts: "Napauta kohtaa, johon sattuu",
    tapBodyPartsHint: "Lisää kipupaikat napauttamalla kehon osia. Värit näyttävät kivun voimakkuuden.",
    swipeHint: "Vaihda Etu/Taka painikkeilla tai pyyhkäisemällä vasemmalle/oikealle. Napauta lisätäksesi kipupaikan.",
    front: "Etu",
    back: "Taka",
    diagramInstruction: "Napauta kehon aluetta säätääksesi sen kipua",
    diagramInstructionMobile: "Napauta kehon aluetta, säädä kipu ja paina OK. Takaosassa voit valita vasen/keskellä/oikea. Vaihda Etu/Taka painikkeilla tai pyyhkäisemällä.",
    painLocations: "Kipupaikat",
    noPainLocations: "Kipupaikkoja ei ole vielä lisätty.",
    addLocationHint: "Lisää kipupaikkoja napauttamalla kehokuvaa tai käyttämällä alla olevaa lomaketta.",
    bodyRegion: "Kehon alue",
    surface: "Pinta",
    addLocation: "Lisää kipupaikka",
    addPainLocation: "Lisää kipupaikka",
    medicationsOptional: "Lääkkeet (valinnainen)",
    notesOptional: "Muistiinpanot (valinnainen)",
    tagsOptional: "Tagit (valinnainen)",
    optional: "valinnainen",
    done: "Valmis",
    quickAdjust: {
      title: "Kipu alueella {{location}}",
      description: "Aseta kivun voimakkuus ja lisää halutessasi muistiinpanot tälle alueelle.",
      intensityLabel: "Kivun voimakkuus",
      notesLabel: "Muistiinpanot (valinnainen)",
      confirm: "OK",
      confirmMany: "Lisää {{count}} kipupaikkaa",
      sideLabel: "Puoli",
      sideHint: "Valitse, kummalla puolella selkää kipu on.",
      sideHintMany: "Valitse kaikki selän puolet, joihin kipu vaikuttaa. Samat kivun tiedot käytetään jokaiseen.",
    },
    qualities: {
      label: "Miltä se tuntuu?",
      placeholder: "Miltä se tuntuu?",
      optionsAriaLabel: "Kivun tuntemusten valinnat",
    },
    locationNotes: {
      add: "Lisää muistiinpanot",
      placeholder: "Kuvaile tätä kipua tarkemmin (mikä laukaisee sen, milloin se on pahempi jne.)",
      inputAriaLabel: "Sijainnin muistiinpanot",
      clear: "Tyhjennä",
    },
    medicationInput: {
      title: "Kipulääkkeet",
      addMedication: "Lisää lääke",
      helpText: "Lisää tähän yksittäiset kipuun otetut lääkkeet tältä päivältä.",
      oneOffInfo: "Nämä ovat kertaluonteisia lääkemerkintöjä tälle kirjaukselle. Säännölliset lääkitykset kuuluvat Lääkitykset-välilehdelle.",
      oneOffInfoAria: "Näytä kertaluonteisten lääkkeiden ohje",
      tookTitle: "Otin {{name}}",
      noActiveMedications: "Lääkkeitä ei ole vielä lisätty",
      namePlaceholder: "Lääkkeen nimi (esim. Ibuprofeeni)",
      nameComboboxAria: "Lääkkeen nimi ehdotuksilla",
      suggestionUsedCount: "käytetty {{count}}x",
      noSuggestions: "Ei osumia — kirjoita lisätäksesi uuden",
      suggestionsSectionLabel: "Aiemmat lääkkeet",
      removeMedicationAria: "Poista lääke",
      dose: "Annos",
      unit: "Yksikkö",
      when: "Milloin",
      notes: "Muistiinpanot",
      addNotes: "Lisää muistiinpano",
      notesPlaceholder: "Valinnaiset tiedot (esim. otettu eilen, auttoi 30 min jälkeen)",
      // Start-medication sheet
      startMedication: {
        title: "Lisää kertaluonteinen lääke",
        description: "Tämä kirjaa kipuun otetun kertaluonteisen lääkkeen ja luo siihen liittyvän tapahtuman.",
        name: "Lääkkeen nimi",
        namePlaceholder: "esim. Ibuprofeeni",
        dose: "Annos",
        dosePlaceholder: "esim. 200",
        unit: "Yksikkö",
        selectUnit: "Yksikkö",
        when: "Vuorokaudenaika",
        selectTime: "Milloin",
        save: "Lisää lääke",
        cancel: "Peruuta",
        saving: "Tallennetaan...",
      },
      // Stop medication dialog (inline check-in)
      stopMedication: {
        title: "Lopeta lääke",
        description: "Aseta päivämäärä, jolloin lääke lopetettiin.",
        dateLabel: "Lopetuspäivä",
        confirm: "Lopeta lääke",
        confirming: "Lopetetaan...",
        cancel: "Peruuta",
        stopAria: "Lopeta {{name}}",
      },
      // Switch medication dialog (inline check-in)
      switchMedication: {
        title: "Vaihda lääke",
        description: "Vaihda lääkkeestä {{from}} uuteen lääkkeeseen.",
        dateLabel: "Vaihtopäivä",
        toLabel: "Mihin (uusi lääke)",
        toPlaceholder: "Hae tai kirjoita lääkkeen nimi...",
        notesLabel: "Muistiinpanot (valinnainen)",
        notesPlaceholder: "Muistiinpanoja vaihdosta...",
        confirm: "Vaihda lääke",
        confirming: "Vaihdetaan...",
        cancel: "Peruuta",
        switchAria: "Vaihda {{name}}",
      },
      // Overflow menu
      moreActions: "Lisätoiminnot: {{name}}",
      // Adherence note dialog (T2.11)
      adherenceNote: {
        menuItem: "Kirjaa huomio",
        title: "Kirjaa huomio lääkityksestä",
        description: "Kirjaa huomio lääkkeen {{name}} ottamisesta.",
        noteLabel: "Huomio",
        notePlaceholder: "esim. Aamuannos jäi väliin matkustamisen vuoksi",
        submit: "Tallenna huomio",
        submitting: "Tallennetaan...",
        cancel: "Peruuta",
      },
    },
    tagsInput: {
      title: "Tagit (valinnainen)",
      placeholder: "Kirjoita tagi ja paina Enter...",
      addTagAria: "Lisää tagi",
      removeTagAria: "Poista tagi {{tag}}",
      suggestions: "Ehdotukset:",
      count: "{{count}}/{{max}} tagia valittu",
      maxReached: "(maksimi saavutettu)",
      commonTags: {
        migraine: "migreeni",
        "flare-up": "pahenemisvaihe",
        "high stress": "korkea stressi",
        "poor sleep": "huono uni",
        "weather change": "sään muutos",
        exercise: "liikunta",
        "work day": "työpäivä",
        travel: "matkustaminen",
        menstrual: "kuukautiset",
        anxiety: "ahdistus",
        fatigue: "väsymys",
        "good day": "hyvä päivä",
      },
    },
    notesPlaceholder: "Lisää mahdolliset lisämuistiinpanot kivusta, syistä tai helpottavista tekijöistä...",
    filledCount: "{{count}} täytetty",
    savePainFreeDay: "Tallenna kivuton päivä",
    locationSingle: "kipupaikka",
    locationPlural: "kipupaikkaa",
    typeSingle: "tyyppi",
    typePlural: "tyyppiä",
    removeLocation: "Poista {{location}}",
    // Daily Impact section
    dailyImpact: {
      sectionTitle: "Päivittäinen vaikutus (valinnainen)",
      sectionDescription: "Miten kipu vaikutti päivääsi?",
      functionalImpactLabel: "Toimintakyvyn vaikutus",
      dimensions: {
        sleep: "Uni",
        mood: "Mieliala",
        mobility: "Liikkuvuus",
        generalActivity: "Yleinen aktiivisuus",
        enjoymentOfLife: "Elämästä nauttiminen",
        work: "Työ",
        relationships: "Ihmissuhteet",
      },
      dimensionScale: {
        none: "Ei vaikutusta",
        full: "Täydellinen",
      },
      symptomsLabel: "Muut oireet",
      fatigue: "Väsymys",
      cognitiveFog: "Aivosumu",
      nausea: "Pahoinvointi",
      vomiting: "Oksentaminen",
      weakness: "Heikotus",
      fatigueScale: {
        none: "Ei",
        full: "Vakava",
      },
      fogScale: {
        none: "Ei",
        full: "Vakava",
      },
      activityPacing: "Aktiivisuustaso",
      pacingLevels: {
        rest: "Lepo",
        light: "Kevyt",
        moderate: "Kohtalainen",
        heavy: "Raskas",
      },
    },
    intensity: {
      none: "Ei kipua",
      mild: "Lievä",
      moderate: "Kohtalainen",
      severe: "Voimakas",
      worst: "Pahin kuviteltavissa",
      worstShort: "Pahin",
    },
    errors: {
      duplicateLocation: "Tämä kehon alue ja puoli on jo lisätty",
      missingLocation: "Lisää vähintään yksi kipupaikka tai kirjaa kivuton päivä",
      saveFailed: "Kipukirjauksen tallennus epäonnistui",
    },
  },

  // --- Validation errors (use-copilot-pain-actions.ts, copilot-action-handlers.ts) ---
  validation: {
    intensityRange: "Voimakkuuden on oltava välillä 0–10",
    locationRequired: "Kipupaikka vaatii alueen tai vyöhykkeen",
    invalidBodyRegion: "Virheellinen kehon alue: {{value}}",
    invalidBodyZone: "Virheellinen kehon vyöhyke: {{value}}",
    invalidLaterality: "Virheellinen lateraalisuus: {{value}}",
    invalidSide: "Virheellinen puoli: {{value}}",
    invalidSurface: "Virheellinen pinta: {{value}}",
    medicationNameRequired: "Lääkkeen nimi on pakollinen",
    invalidTimeOfDay: "Virheellinen vuorokaudenaika: {{value}}",
    invalidEventType: "Virheellinen tapahtumatyyppi: {{value}}. Käytä jotakin seuraavista: {{validTypes}}",
    eventTitleRequired: "Tapahtuman otsikko on pakollinen",
    eventTitleMaxLength: "Tapahtuman otsikko saa olla enintään 100 merkkiä",
    invalidConfidenceLevel: "Virheellinen luottamustaso: {{value}}",
  },

  // --- Hook-level messages (use-pain-entry.ts, use-save-update-set.ts) ---
  hooks: {
    failedToSave: "Tallennus epäonnistui",
  },
} as const;
